import { useState, useEffect } from "react";

import "./TemplateModalForm.scss";

// NEW:
// Creates a blank task object in the same shape expected by your backend.
// Backend/Firebase will create taskID/dateCreated/dateModified for new tasks.
const createEmptyTemplateTask = (order = 0) => {
  return {
    taskID: "",
    title: "",
    description: "",
    order,
    dateCreated: "",
    dateModified: "",
  };
};

// NEW:
// Converts Firebase/backend template tasks into modal form state.
// This preserves taskID/dateCreated/dateModified when editing an existing template.
const mapTemplateTasksToFormTasks = (templateTasks = []) => {
  if (templateTasks.length === 0) {
    return [createEmptyTemplateTask()];
  }

  return templateTasks.map((task, index) => ({
    taskID: task.taskID || "",
    title: task.title || "",
    description: task.description || "",
    order: typeof task.order === "number" ? task.order : index,
    dateCreated: task.dateCreated || "",
    dateModified: task.dateModified || "",
  }));
};

const TemplateModalForm = ({
  onClose,

  // PASSED FROM TemplateDashboardContent:
  // This function eventually calls addTemplate() or editTemplate()
  // through TemplateContext/backend/Firebase.
  onSave,

  // Passed from TemplateDashboardContent when viewing/editing an existing template.
  selectedTemplateData,

  // Controls create/edit mode vs view mode.
  isFormEditMode,
  setIsFormEditMode,
}) => {
  const templateAlreadyExists = Boolean(selectedTemplateData?.id);

  const [templateName, setTemplateName] = useState(
    selectedTemplateData?.name || "",
  );

  const [templateDescription, setTemplateDescription] = useState(
    selectedTemplateData?.description || "",
  );

  const [templateTag, setTemplateTag] = useState(
    selectedTemplateData?.tag || "",
  );

  // UPDATED:
  // Uses metadata-preserving mapper instead of only keeping title/description.
  const [tasks, setTasks] = useState(
    mapTemplateTasksToFormTasks(selectedTemplateData?.tasks || []),
  );

  useEffect(() => {
    document.body.classList.add("template-modal-form-body-open");

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.body.classList.remove("template-modal-form-body-open");
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onClose]);

  // UPDATED:
  // When selectedTemplateData changes, repopulate the form while preserving
  // taskID/dateCreated/dateModified for existing tasks.
  useEffect(() => {
    setTemplateName(selectedTemplateData?.name || "");
    setTemplateDescription(selectedTemplateData?.description || "");
    setTasks(mapTemplateTasksToFormTasks(selectedTemplateData?.tasks || []));

    // NEW:
    // Load existing tag into edit mode, or reset to empty for new template.
    setTemplateTag(selectedTemplateData?.tag || "");
  }, [selectedTemplateData]);

  const handleEditClick = () => {
    setIsFormEditMode(true);
  };

  // UPDATED:
  // Keeps existing task metadata when editing a field.
  const handleTaskChange = (index, field, value) => {
    const updatedTasks = [...tasks];

    updatedTasks[index] = {
      ...updatedTasks[index],
      [field]: value,
    };

    setTasks(updatedTasks);
  };

  // UPDATED:
  // Adds a new blank task with the correct shape.
  // Backend will assign permanent taskID/dateCreated/dateModified.
  const handleAddTask = () => {
    setTasks([...tasks, createEmptyTemplateTask(tasks.length)]);
  };

  // UPDATED:
  // Removes task and reindexes order.
  // Existing taskID/dateCreated/dateModified are preserved for remaining tasks.
  const handleRemoveTask = (index) => {
    const updatedTasks = tasks
      .filter((_, taskIndex) => taskIndex !== index)
      .map((task, taskIndex) => ({
        ...task,
        order: taskIndex,
      }));

    setTasks(
      updatedTasks.length > 0 ? updatedTasks : [createEmptyTemplateTask()],
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // UPDATED:
    // Keep task metadata in the payload so backend can preserve existing
    // taskID/dateCreated and only update dateModified when a task changes.
    const cleanedTasks = tasks
      .map((task, index) => ({
        taskID: task.taskID || "",
        title: task.title.trim(),
        description: task.description.trim(),
        order: index,
        dateCreated: task.dateCreated || "",
        dateModified: task.dateModified || "",
      }))
      .filter((task) => task.title);

    if (!templateName.trim()) {
      alert("Please add a template name.");
      return;
    }

    if (cleanedTasks.length === 0) {
      alert("Please add at least one task title.");
      return;
    }

    // UPDATED:
    // This payload is passed upward to TemplateDashboardContent.
    // TemplateDashboardContent then calls addTemplate() or editTemplate().
    const templatePayload = {
      id: selectedTemplateData?.id,
      name: templateName.trim(),
      description: templateDescription.trim(),
      tasks: cleanedTasks,

      // UPDATED:
      //  Send the selected/custom form tag.
      // not selectedTemplateData.tag.
      tag: templateTag.trim(),
    };

    // console.log("TEMPLATE MODAL - PAYLOAD SENT TO DASHBOARD:", templatePayload);

    onSave(templatePayload);
  };

  return (
    <div className="template-modal-form-overlay" onClick={onClose}>
      <div
        className="template-modal-form-side-panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="template-modal-form-header">
          <div>
            <h2>
              {templateAlreadyExists
                ? isFormEditMode
                  ? "Update Template Details"
                  : "Template Details"
                : "Create New Template"}
            </h2>

            <p>
              {templateAlreadyExists
                ? isFormEditMode
                  ? "Update this reusable routine, checklist, or process."
                  : "Review this reusable routine, checklist, or process."
                : "Create a reusable routine, checklist, or process."}
            </p>
          </div>

          <button
            type="button"
            className="template-modal-form-close-button"
            onClick={onClose}
          >
            X
          </button>
        </div>

        {/* VIEW MODE */}
        {!isFormEditMode && (
          <div className="template-modal-form-view">
            <div className="template-modal-form-view-row">
              <label>Template Name</label>

              <div className="template-modal-form-view-field">
                {selectedTemplateData?.name || "-"}
              </div>
            </div>

            <div className="template-modal-form-view-row">
              <label>Description</label>

              <div className="template-modal-form-description-view">
                {selectedTemplateData?.description || "-"}
              </div>
            </div>

            <div className="template-modal-form-view-row">
              <label>Template Tasks</label>

              <div className="template-modal-form-view-task-list">
                {selectedTemplateData?.tasks?.length > 0 ? (
                  selectedTemplateData.tasks.map((task, index) => (
                    <div
                      className="template-modal-form-view-task-row"
                      key={task.taskID || index}
                    >
                      <span>{index + 1}</span>
                      <p>{task.title}</p>
                    </div>
                  ))
                ) : (
                  <div className="template-modal-form-view-field">-</div>
                )}
              </div>
            </div>

            <div className="template-modal-form-footer">
              <button
                type="button"
                className="template-modal-form-edit-button"
                onClick={handleEditClick}
              >
                Edit
              </button>
            </div>
          </div>
        )}

        {/* CREATE / EDIT MODE */}
        {isFormEditMode && (
          <form className="template-modal-form-content" onSubmit={handleSubmit}>
            <div className="template-modal-form-section">
              <label className="template-modal-form-label">
                Template Name <span>*</span>
              </label>

              <input
                className="template-modal-form-input"
                type="text"
                placeholder="e.g. Client Onboarding Checklist"
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
              />
            </div>

            <div className="template-modal-form-section">
              <label className="template-modal-form-label">
                Description <small> (Optional) </small>
              </label>

              <textarea
                className="template-modal-form-textarea"
                placeholder="Short note about when to use this template"
                value={templateDescription}
                onChange={(event) => setTemplateDescription(event.target.value)}
              />
            </div>

            {/* Tag section intentionally placed AFTER the task list */}
            <div className="template-modal-form-section">
              <label className="template-modal-form-label">
                Tag Label <small> (Optional) </small>
              </label>

              <div className="template-tag-line">
                <button
                  type="button"
                  className={`template-tag-chip routine ${
                    templateTag === "Routine" ? "active" : ""
                  }`}
                  onClick={() => setTemplateTag("Routine")}
                >
                  Routine
                </button>

                <button
                  type="button"
                  className={`template-tag-chip checklist ${
                    templateTag === "Checklist" ? "active" : ""
                  }`}
                  onClick={() => setTemplateTag("Checklist")}
                >
                  Checklist
                </button>

                <button
                  type="button"
                  className={`template-tag-chip process ${
                    templateTag === "Process" ? "active" : ""
                  }`}
                  onClick={() => setTemplateTag("Process")}
                >
                  Process
                </button>

                <button
                  type="button"
                  className={`template-tag-chip custom ${
                    templateTag !== "Routine" &&
                    templateTag !== "Checklist" &&
                    templateTag !== "Process"
                      ? "active"
                      : ""
                  }`}
                  onClick={() => setTemplateTag("")}
                >
                  Custom
                </button>

                {templateTag !== "Routine" &&
                  templateTag !== "Checklist" &&
                  templateTag !== "Process" && (
                    <div className="template-custom-input-wrapper">
                      <input
                        type="text"
                        value={templateTag}
                        onChange={(event) => setTemplateTag(event.target.value)}
                        placeholder="Enter a custom tag"
                        className="template-custom-tag-input"
                        autoFocus
                      />
                    </div>
                  )}
              </div>
            </div>

            <div className="template-modal-form-tasks-header">
              <div>
                <h3>Template Tasks</h3>
                <p>Add the repeatable tasks this template should create</p>
              </div>

              <button
                type="button"
                className="template-modal-form-add-task-button"
                onClick={handleAddTask}
              >
                + Add Task
              </button>
            </div>

            <div className="template-modal-form-task-list">
              {tasks.map((task, index) => (
                <div
                  className="template-modal-form-task-row"
                  key={task.taskID || index}
                >
                  <div className="template-modal-form-task-number">
                    {index + 1}
                  </div>

                  <div className="template-modal-form-task-fields">
                    <div className="template-modal-form-section">
                      <label className="template-modal-form-label">
                        Task Title <span>*</span>
                      </label>

                      <input
                        className="template-modal-form-input"
                        type="text"
                        placeholder="e.g. Send intro email"
                        value={task.title}
                        onChange={(event) =>
                          handleTaskChange(index, "title", event.target.value)
                        }
                      />
                    </div>
                  </div>

                  {tasks.length > 1 && (
                    <button
                      type="button"
                      className="template-modal-form-remove-task-button"
                      onClick={() => handleRemoveTask(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="template-modal-form-footer">
              <button
                type="button"
                className="template-modal-form-cancel-button"
                onClick={onClose}
              >
                Cancel
              </button>

              <button type="submit" className="template-modal-form-save-button">
                {templateAlreadyExists ? "Save Changes" : "Save Template"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TemplateModalForm;
